import React, { useEffect, useState } from 'react';
import contactUs from '../assets/img/contact-us.png';
import { postApi } from '../api';
import Button from '../components/ui/Button';
import toast from 'react-hot-toast';

function Contact() {
	const [formData, setFormData] = useState({
		name: '',
		email: '',
		message: ''
	});
	const [errors, setErrors] = useState({});
	const [isLoading, setIsLoading] = useState(false);
	const [isSubmitted, setIsSubmitted] = useState(false);

	useEffect(() => {
		window.scrollTo(0, 0);
	}, []);

	const validateForm = () => {
		const newErrors = {};
		
		if (!formData.name.trim()) {
			newErrors.name = 'Name is required';
		} else if (formData.name.length > 255) {
			newErrors.name = 'Name cannot exceed 255 characters';
		}

		if (!formData.email.trim()) {
			newErrors.email = 'Email is required';
		} else if (formData.email.length > 254) {
			newErrors.email = 'Email cannot exceed 254 characters';
		} else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(formData.email)) {
			newErrors.email = 'Invalid email address';
		}

		if (!formData.message.trim()) {
			newErrors.message = 'Message is required';
		}

		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	};

	const handleChange = (e) => {
		const { name, value } = e.target;
		setFormData(prev => ({
			...prev,
			[name]: value
		}));
		// Clear error when user starts typing
		if (errors[name]) {
			setErrors(prev => ({
				...prev,
				[name]: ''
			}));
		}
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		
		if (!validateForm()) {
			return;
		}

		setIsLoading(true);
		try {
			const response = await postApi('system/inquiries/', formData);
			
			if (response.status === false) {
				throw new Error(response.error || 'Failed to send message');
			}

			setIsSubmitted(true);
			toast.success('Message sent successfully!');
			// Clear form after successful submission
			setFormData({
				name: '',
				email: '',
				message: ''
			});
		} catch (error) {
			toast.error(error.message || 'Failed to send message');
		} finally {
			setIsLoading(false);
		}
	};

	const SuccessMessage = () => (
		<div className="flex flex-col items-center justify-center space-y-4 text-center p-8">
			<div className="w-16 h-16 bg-brand-100 rounded-full flex items-center justify-center">
				<svg 
					className="w-8 h-8 text-brand-500" 
					fill="none" 
					stroke="currentColor" 
					viewBox="0 0 24 24"
				>
					<path 
						strokeLinecap="round" 
						strokeLinejoin="round" 
						strokeWidth="2" 
						d="M5 13l4 4L19 7"
					/>
				</svg>
			</div>
			<h3 className="text-2xl font-semibold text-gray-800">Message Sent Successfully!</h3>
			<p className="text-gray-600 max-w-md">
				Thank you for contacting us. You'll receive a response on your email shortly.
			</p>
		</div>
	);

	return (
		<div className="bg-gray-50">
			<div className="flex justify-center py-10 lg:py-20 bg-page-header-bg bg-brand-100 w-full bg-cover bg-no-repeat bg-bottom bg-page-header ">
				<div className="flex mx-auto w-full max-w-screen-2xl px-3 sm:px-10">
					<div className="w-full flex justify-center flex-col relative">
						<h2 className="text-xl md:text-3xl lg:text-4xl font-bold text-center text-black">
							Contact Us
						</h2>
					</div>
				</div>
			</div>
			<div className="bg-white">
				<div className="max-w-screen-2xl mx-auto lg:py-20 py-10 px-4 sm:px-10">
					<div className="grid md:grid-cols-2 gap-6 lg:grid-cols-3 xl:gap-8 ">
						<div className="border p-10 rounded-lg text-center">
							<span className="flex justify-center text-4xl text-brand-500 mb-4 ">
								<svg
									xmlns="http://www.w3.org/2000/svg"
									fill="none"
									viewBox="0 0 24 24"
									stroke="currentColor"
									strokeWidth="2"
									height="1em"
									width="1em"
								>
									<path
										strokeLinecap="round"
										strokeLinejoin="round"
										d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
									/>
								</svg>
							</span>
							<h5 className="text-xl mb-2 font-bold">Email Us</h5>
							<p className="mb-0 text-base opacity-90 leading-7">
								<a
									href="mailto:info@wholesaledai.com"
									className="text-brand-600"
								>
									info@wholesaledai.com{' '}
								</a>
								We reply within one business day.
							</p>
						</div>
						<div className="border p-10 rounded-lg text-center">
							<span className="flex justify-center text-4xl text-brand-500 mb-4">
								<svg
									xmlns="http://www.w3.org/2000/svg"
									fill="none"
									viewBox="0 0 24 24"
									stroke="currentColor"
									strokeWidth="2"
									height="1em"
									width="1em"
								>
									<path
										strokeLinecap="round"
										strokeLinejoin="round"
										d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
									/>
								</svg>
							</span>
							<h5 className="text-xl mb-2 font-bold">Call Us</h5>
							<p className="mb-0 text-base opacity-90 leading-7">
								<a href="tel:+9779851448066" className="text-brand-600">
									+977-9851448066{' '}
								</a>
								Sunday to Friday, 9am–6pm.
							</p>
						</div>
						<div className="border p-10 rounded-lg text-center">
							<span className="flex justify-center text-4xl text-brand-500 mb-4">
								<svg
									xmlns="http://www.w3.org/2000/svg"
									fill="none"
									viewBox="0 0 24 24"
									stroke="currentColor"
									strokeWidth="2"
									height="1em"
									width="1em"
								>
									<path
										strokeLinecap="round"
										strokeLinejoin="round"
										d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
									/>
									<path
										strokeLinecap="round"
										strokeLinejoin="round"
										d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
									/>
								</svg>
							</span>
							<h5 className="text-xl mb-2 font-bold">Location</h5>
							<p className="mb-0 text-base opacity-90 leading-7">
								<a href="mailto:" className="text-brand-600">
									Address:
								</a>{' '}
								O2, Newroad, Kathmandu-22, Nepal
							</p>
						</div>
					</div>
					<div className="px-0 pt-24 mx-auto items-center flex flex-col md:flex-row w-full justify-between">
						<div className="hidden md:w-full lg:w-5/12 lg:flex flex-col h-full">
							<span
								style={{
									boxSizing: 'border-box',
									display: 'inline-block',
									overflow: 'hidden',
									width: 'initial',
									height: 'initial',
									background: 'none',
									opacity: '1',
									border: '0px',
									margin: '0px',
									padding: '0px',
									position: 'relative',
									maxWidth: '100%',
								}}
							>
								<span
									style={{
										boxSizing: 'border-box',
										display: 'block',
										width: 'initial',
										height: 'initial',
										background: 'none',
										opacity: '1',
										border: '0px',
										margin: '0px',
										padding: '0px',
										maxWidth: '100%',
									}}
								>
									<img
										alt="contactus"
										aria-hidden="true"
										style={{
											display: 'block',
											maxWidth: '100%',
											width: 'initial',
											height: 'initial',
											background: 'none',
											opacity: '1',
											border: '0px',
											margin: '0px',
											padding: '0px',
										}}
										src={contactUs}
									></img>
								</span>
							</span>
						</div>
						<div className="px-0 pb-2 lg:w-5/12 flex flex-col md:flex-row">
							{isSubmitted ? (
								<SuccessMessage />
							) : (
								<form onSubmit={handleSubmit} className="w-full mx-auto flex flex-col justify-center">
									<div className="mb-12">
										<h3 className="text-xl md:text-2xl lg:text-3xl font-semibold mb-3">
											For any support just send your query
										</h3>
										<p className="text-base opacity-90 leading-7">
											Collaboratively promote client-focused convergence vis-a-vis
											customer directed alignments via plagiarize strategic users
											and standardized infrastructures.
										</p>
									</div>
									<div className="flex flex-col space-y-5">
										<div className="flex flex-col md:flex-row space-y-5 md:space-y-0">
											<div className="w-full md:w-1/2">
												<label className="block text-gray-500 font-medium text-sm leading-none mb-2">
													Your Name *
												</label>
												<div className="relative">
													<input
														name="name"
														type="text"
														value={formData.name}
														onChange={handleChange}
														placeholder="Enter Your Name"
														className={`py-2 px-4 md:px-5 w-full appearance-none border text-sm opacity-75 text-input rounded-md placeholder-body min-h-12 transition duration-200 focus:ring-0 ease-in-out bg-white border-gray-200 focus:outline-none focus:border-brand-500 h-11 md:h-12 ${
															errors.name ? 'border-red-500' : ''
														}`}
													/>
													{errors.name && (
														<p className="text-red-500 text-xs mt-1">{errors.name}</p>
													)}
												</div>
											</div>
											<div className="w-full md:w-1/2 md:ml-2.5 lg:ml-5 mt-2 md:mt-0">
												<label className="block text-gray-500 font-medium text-sm leading-none mb-2">
													Your Email *
												</label>
												<div className="relative">
													<input
														name="email"
														type="email"
														value={formData.email}
														onChange={handleChange}
														placeholder="Enter Your Email"
														className={`py-2 px-4 md:px-5 w-full appearance-none border text-sm opacity-75 text-input rounded-md placeholder-body min-h-12 transition duration-200 focus:ring-0 ease-in-out bg-white border-gray-200 focus:outline-none focus:border-brand-500 h-11 md:h-12 ${
															errors.email ? 'border-red-500' : ''
														}`}
													/>
													{errors.email && (
														<p className="text-red-500 text-xs mt-1">{errors.email}</p>
													)}
												</div>
											</div>
										</div>
										<div className="relative mb-4">
											<label className="block text-gray-500 font-medium text-sm leading-none mb-2">
												Message *
											</label>
											<textarea
												name="message"
												value={formData.message}
												onChange={handleChange}
												className={`px-4 py-3 flex items-center w-full rounded appearance-none opacity-75 transition duration-300 ease-in-out text-sm focus:ring-0 bg-white border border-gray-300 focus:shadow-none focus:outline-none focus:border-brand-500 placeholder-body ${
													errors.message ? 'border-red-500' : ''
												}`}
												rows="4"
												placeholder="Write your message here"
											></textarea>
											{errors.message && (
												<p className="text-red-500 text-xs mt-1">{errors.message}</p>
											)}
										</div>
										<div className="relative">
											<Button type="submit" size="lg" disabled={isLoading} className="mt-1 w-full sm:w-auto">
												{isLoading ? 'Sending...' : 'Send Message'}
											</Button>
										</div>
									</div>
								</form>
							)}
						</div>
					</div>
				</div>
			</div>
		</div>
	);
}

export default Contact;
